<template>
  <div class="calendar-wrapper">
    <!-- Header -->
    <div class="calendar-toolbar">
      <div class="toolbar-nav-group">
        <a-button @click="changeMonth(-1)" class="nav-btn">
          <a-icon type="left"/> 
          <span class="btn-text">Previous</span>
        </a-button>
        <h2 class="calendar-title">
          {{ currentMonth.clone().locale('en').format('MMMM') }} 
          <span class="year">{{ currentMonth.format('YYYY') }}</span>
        </h2>
        <a-button @click="changeMonth(1)" class="nav-btn">
          <span class="btn-text">Next</span>
          <a-icon type="right"/>
        </a-button>
      </div>

      <div class="toolbar-view-group">
        <a-radio-group v-model="viewMode" class="view-toggle-group">
          <a-radio-button value="calendar">
            <a-icon style="font-size: 16px;font-weight: 700;" type="appstore" />
          </a-radio-button>
          <a-radio-button value="list">
            <a-icon style="font-size: 16px;font-weight: 700;" type="bars" />
          </a-radio-button>
        </a-radio-group>
      </div>
    </div>

    <!-- Weekday Header (Desktop only) -->
    <a-row v-if="viewMode === 'calendar'" :gutter="[16, 16]" class="weekdays-row mb-20">
      <a-col
        class="calendar-col"
        v-for="(day, index) in weekdays"
        :key="day"
      >
        <div class="weekday-header" :class="{'weekend-text': index === 0 || index === 6}">{{ day }}</div>
      </a-col>
    </a-row>

    <!-- Calendar Grid -->
    <a-spin :spinning="loading" tip="กำลังโหลดข้อมูล...">
      <div class="calendar-grid" v-if="viewMode === 'calendar'">
        <div v-for="(week, weekIndex) in calendarWeeks" :key="'week-' + weekIndex">
        <a-row :gutter="[16, 16]">
          <a-col
            :xs="24"
            :sm="12"
            :md="8"
            :lg="6"
            class="calendar-col"
            v-for="(item, index) in week"
            :key="index"
          >
            <div class="event-card" :class="{ 'faded-card': !item.isCurrentMonth, 'is-today': item.isToday }">
              <div class="event-card-header" :class="{'weekend-bg': item.isWeekend, 'holiday-bg': item.isHoliday}">
                <div style="display: flex; flex-direction: column; align-items: flex-start; gap: 4px;">
                  <span class="month">{{ item.date.clone().locale('en').format('MMM') }}</span>
                  <div style="display: flex; flex-wrap: wrap; align-items: center;">
                    <template v-if="item.isPayday">
                      <a-tooltip :title="item.paydayDetail || 'Payday'">
                        <a-tag color="green" class="header-tag">
                          <a-icon type="dollar" />
                        </a-tag>
                      </a-tooltip>
                      <span v-if="item.isEmergency || item.isAnnounce" class="tag-separator">,</span>
                    </template>
                    
                    <template v-if="item.isEmergency">
                      <a-tooltip :title="item.emergencyDetail">
                        <a-tag color="volcano" class="header-tag">
                          <a-icon type="warning" />
                        </a-tag>
                      </a-tooltip>
                      <span v-if="item.isAnnounce" class="tag-separator">,</span>
                    </template>
                    
                    <template v-if="item.isAnnounce">
                      <a-tooltip :title="item.announceDetail">
                        <a-tag color="blue" class="header-tag">
                          <a-icon type="notification" />
                        </a-tag>
                      </a-tooltip>
                    </template>
                  </div>
                </div>
                <span class="day">{{ item.date.format('D') }}</span>
              </div>
              
              <!-- Holiday View -->
              <div v-if="item.isHoliday" class="holiday-body">
                <div class="holiday-icon">
                  <a-icon type="api" theme="filled" />
                </div>
                <div class="holiday-detail">{{ item.holidayDetail }}</div>
              </div>
              
              <!-- Standard View -->
              <div v-else class="event-card-body">
                <template v-if="!item.isWeekend && (item.events.length > 0 || item.muicEvents.length > 0)">
                  <a-list v-if="item.events && item.events.length > 0" item-layout="horizontal" :data-source="item.events" :split="false">
                    <a-list-item 
                      slot="renderItem" 
                      slot-scope="eventItem, index" 
                      :style="{ 
                        padding: '4px 8px', 
                        marginBottom: '4px', 
                        borderRadius: '6px', 
                        backgroundColor: getEventMeta(eventItem.type).bgColor 
                      }"
                    >
                      <a-list-item-meta>
                        <p slot="title" :style="{  marginTop: '0px',  marginBottom: '0px',  fontWeight: '600', fontSize: '11px', color: getEventMeta(eventItem.type).color }">
                          {{ eventItem.title }}
                        </p>
                        <a-avatar
                          slot="avatar"
                          size="small"
                          :icon="getEventMeta(eventItem.type).icon"
                          :style="{ 
                            backgroundColor: 'white', 
                            color: getEventMeta(eventItem.type).color,
                            verticalAlign: 'middle' 
                          }"
                        />
                      </a-list-item-meta>
                    </a-list-item>
                  </a-list>
                  <div v-if="item.muicEvents && item.muicEvents.length > 0" class="event-action" style="margin-top: 8px;">
                    <a-popover placement="top" trigger="click">
                      <template slot="content">
                        <div style="max-height: 350px; overflow-y: auto; width: 300px; padding: 4px;">
                          <div v-for="(muicEvent, idx) in item.muicEvents" :key="idx" :style="{ padding: '10px', borderBottom: idx === item.muicEvents.length - 1 ? 'none' : '1px solid #f1f5f9' }">
                            <div style="font-weight: 600; color: #1e293b; line-height: 1.4; margin-bottom: 4px;">{{ muicEvent.name }}</div>
                            <div style="display: flex; align-items: center; font-size: 11px; color: #64748b;">
                              <a-icon type="clock-circle" style="margin-right: 4px;" />
                              <span>{{ moment(muicEvent.start_date).format('HH:mm') }} - {{ moment(muicEvent.end_date).format('HH:mm') }}</span>
                            </div>
                          </div>
                        </div>
                      </template>
                      <template slot="title">
                        <span style="font-weight: 700; color: #6a40bf;">MUIC Events</span>
                      </template>
                      <div class="muic-event-trigger">
                        <a-icon type="calendar" style="margin-right: 6px; font-size: 11px;" />
                        <span>Events Of MUIC ({{ item.muicEvents.length }})</span>
                      </div>
                    </a-popover>
                  </div>
                </template>
              </div>
              <!-- Card Actions (Time IN / OUT) -->
              <div class="event-card-actions" v-if="isStaff">
                <div class="action-item">
                  <span>Time IN</span>
                  <span>{{ item.timeIn }}</span>
                </div>
                <div class="action-item">
                  <span>Time OUT</span>
                  <span :class="{'estimated-time': item.isTimeOutEstimated}">{{ item.timeOut }}</span>
                </div>
              </div>
            </div>
          </a-col>
        </a-row>
        <!-- Horizontal Divider between rows -->
        <a-divider 
          v-if="weekIndex < calendarWeeks.length - 1" 
          style="margin: 24px 0; border-color: #d9d9d9; border-width: 2px;" 
        />
        </div>
      </div>

      <!-- List View -->
      <div class="list-view-container" v-else>
        <a-list item-layout="vertical" size="large" :data-source="calendarDays.filter(d => d.isCurrentMonth)" :split="false">
          <a-list-item slot="renderItem" :key="item.date.format('YYYY-MM-DD')" slot-scope="item">
            <div class="list-item-card" :class="{ 'list-today': item.isToday, 'list-weekend': item.isWeekend, 'list-holiday': item.isHoliday }">
              <div class="list-date-info">
                <div class="list-day-num">{{ item.date.format('DD') }}</div>
                <div class="list-month-weekday">
                  <div class="list-weekday">{{ item.date.format('dddd') }}</div>
                  <div class="list-month">{{ item.date.format('MMMM YYYY') }}</div>
                </div>
                <div class="list-tags">
                  <a-tooltip v-if="item.isPayday" :title="item.paydayDetail || 'Payday'">
                    <a-tag color="green" style="font-weight: 700; border-radius: 4px; border: none;">
                      <a-icon type="dollar" /> PAYDAY
                    </a-tag>
                  </a-tooltip>
                  <a-tooltip v-if="item.isEmergency" :title="item.emergencyDetail">
                    <a-tag color="volcano" style="font-weight: 700; border-radius: 4px; border: none;">
                      <a-icon type="warning" /> EMERGENCY
                    </a-tag>
                  </a-tooltip>
                  <a-tooltip v-if="item.isAnnounce" :title="item.announceDetail">
                    <a-tag color="blue" style="font-weight: 700; border-radius: 4px; border: none;">
                      <a-icon type="notification" /> ANNOUNCE
                    </a-tag>
                  </a-tooltip>
                  <a-tag v-if="item.isHoliday" color="red" style="font-weight: 700; border-radius: 4px; border: none;">
                    <a-icon type="api" /> HOLIDAY
                  </a-tag>
                </div>
              </div>

              <div class="list-content-main">
                <!-- Records/Events -->
                <div class="list-events-section">
                  <div v-if="item.isHoliday" class="holiday-text-list">
                    {{ item.holidayDetail }}
                  </div>
                  <template v-else>
                    <div v-for="(event, idx) in item.events" :key="'evt-'+idx" class="compact-event-row" :style="{ backgroundColor: getEventMeta(event.type).bgColor, borderLeft: '4px solid ' + getEventMeta(event.type).color }">
                       <a-icon :type="getEventMeta(event.type).icon" :style="{ color: getEventMeta(event.type).color, marginRight: '8px' }" />
                       <span :style="{ color: getEventMeta(event.type).color, fontWeight: '600' }">{{ event.title }}</span>
                    </div>
                    <div v-for="(muicEvent, idx) in item.muicEvents" :key="'muic-'+idx" class="compact-event-row muic-event-row">
                       <a-icon type="calendar" style="margin-right: 8px; color: #6a40bf;" />
                       <div style="flex-grow: 1; display: flex; justify-content: space-between; align-items: center;">
                         <span>{{ muicEvent.name }}</span>
                         <span style="font-size: 11px; color: #64748b; font-weight: 600; margin-left: 12px; white-space: nowrap;">
                           {{ moment(muicEvent.start_date).format('HH:mm') }} - {{ moment(muicEvent.end_date).format('HH:mm') }}
                         </span>
                       </div>
                    </div>
                  </template>
                </div>

                <!-- Time IN/OUT -->
                <div class="list-time-records" v-if="isStaff">
                  <div class="time-record-box">
                    <span class="label">Time IN</span>
                    <span class="value">{{ item.timeIn }}</span>
                  </div>
                  <div class="time-record-box">
                    <span class="label">Time OUT</span>
                    <span class="value" :class="{'estimated-time': item.isTimeOutEstimated}">{{ item.timeOut }}</span>
                  </div>
                </div>
              </div>
            </div>
          </a-list-item>
        </a-list>
      </div>
    </a-spin>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: "MainEvent",
  data() {
    return {
      currentMonth: moment(),
      calendarDays: [],
      weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      loading: false,
      viewMode: 'calendar',
      lastIsSmall: null
    };
  },
  computed: {
    calendarWeeks() {
      const weeks = [];
      for (let i = 0; i < this.calendarDays.length; i += 7) {
        weeks.push(this.calendarDays.slice(i, i + 7));
      }
      return weeks;
    },
    isStaff() {
      const { RulesUser } = this.$store.state.role || {};
      return RulesUser && RulesUser.roles && RulesUser.roles.includes('staff');
    }
  },
  mounted() {
    this.checkViewMode();
    window.addEventListener('resize', this.checkViewMode);
    this.loadCalendarData();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkViewMode);
  },
  methods: {
    generateCalendar() {
      // Start from the first day of the week of the first day of the month
      const startDay = this.currentMonth.clone().startOf('month').startOf('week');
      // End at the last day of the week of the last day of the month
      const endDay = this.currentMonth.clone().endOf('month').endOf('week');
      
      const days = [];
      let day = startDay.clone();
      
      while (day.isSameOrBefore(endDay, 'day')) {
        
        days.push({
          date: day.clone(),
          isCurrentMonth: day.month() === this.currentMonth.month(),
          isToday: day.isSame(moment(), 'day'),
          isWeekend: day.day() === 0 || day.day() === 6,
          events: [],
          timeIn: '-',
          timeOut: '-',
          isTimeOutEstimated: false,
          isHoliday: false,
          holidayDetail: '',
          isPayday: false,
          paydayDetail: '',
          isEmergency: false,
          emergencyDetail: '',
          isAnnounce: false,
          announceDetail: '',
          muicEvents: []
        });
        day.add(1, 'd');
      }
      
      this.calendarDays = days;
    },
    changeMonth(step) {
      this.currentMonth = this.currentMonth.clone().add(step, 'months');
      this.loadCalendarData();
    },
    async fetchRequestData(startDateStr, endDateStr) {
      const endpoints = [
        { key: 'leave', url: `/hri/leave_of_absence/?timestart=${startDateStr}&timeend=${endDateStr}` },
        { key: 'seminar', url: `/hri/seminar-request/?timestart=${startDateStr}&timeend=${endDateStr}` }
      ];

      if (this.isStaff) {
        endpoints.push({ key: 'flexible', url: `/hri/flexible-day/me/?start=${startDateStr}&end=${endDateStr}` });
        endpoints.push({ key: 'wfh', url: `/hri/workfromhome/me/?start=${startDateStr}&end=${endDateStr}` });
      }

      try {
        const promiseArray = endpoints.map(item => 
          this.axios.get(item.url)
            .then(res => ({ key: item.key, data: res.data }))
            .catch(() => ({ key: item.key, data: [] }))
        );
        const resultsArray = await Promise.all(promiseArray);
        const eventMap = {};

        resultsArray.forEach(result => {
          let records = Array.isArray(result.data) ? result.data : (result.data.results || []);
          records = records.filter(record => record.status && record.status.toLowerCase() === 'approve');

          records.forEach(record => {
            const mappedEvent = this.mapHriRecordToEvent(result.key, record);
            if (mappedEvent && mappedEvent.start && mappedEvent.end) {
              let currentMomentDate = moment(mappedEvent.start).format('YYYY-MM-DD');
              const lastMomentDate = moment(mappedEvent.end).format('YYYY-MM-DD');

              while (moment(currentMomentDate).isSameOrBefore(lastMomentDate, 'day')) {
                if (!eventMap[currentMomentDate]) {
                  eventMap[currentMomentDate] = [];
                }
                eventMap[currentMomentDate].push({ 
                  title: mappedEvent.title, 
                  desc: '', 
                  type: mappedEvent.type 
                });
                currentMomentDate = moment(currentMomentDate).add(1, 'days').format('YYYY-MM-DD');
              }
            }
          });
        });

        return eventMap;
      } catch (err) {
        console.error("Error fetching request data:", err);
        return {};
      }
    },
    mapHriRecordToEvent(key, record) {
      let start, end, title, type;

      if (key === 'leave') {
        start = record.start_date;
        end = record.end_date || record.start_date;
        title = record.absence_type ? (record.absence_type.charAt(0).toUpperCase() + record.absence_type.slice(1)) : 'Leave Request';
        type = 'Leave Of Absence';
      } else if (key === 'flexible') {
        start = record.selected_date;
        end = record.selected_date;
        title = 'Flexible Day';
        type = 'Flexible Day';
      } else if (key === 'wfh') {
        start = record.selected_date;
        end = record.selected_date;
        title = 'Work From Home';
        type = 'Work From Home';
      } else if (key === 'seminar') {
        start = record.start_date;
        end = record.end_date || record.start_date;
        title = 'Seminar';
        type = 'Seminar';
      }

      return { start, end, title, type };
    },
    async loadCalendarData() {
      this.generateCalendar();

      if (this.calendarDays.length === 0) return;
      this.loading = true;

      const firstDate = this.calendarDays[0].date.clone();
      const lastDate = this.calendarDays[this.calendarDays.length - 1].date.clone();

      const apiDates = {
        attendStart: firstDate.format('YYYY-MM-DDT00:00:00+07:00'),
        attendEnd: lastDate.format('YYYY-MM-DDT23:59:59+07:00'),
        calStart: firstDate.format('YYYY-MM-DD'),
        calEnd: lastDate.format('YYYY-MM-DD'),
        smartStart: firstDate.format('YYYY-MM-DDT00:00:00'),
        smartEnd: lastDate.format('YYYY-MM-DDT23:59:59')
      };

      try {
        const urlAttend = `/hri/time_attendance/?created_at__gte=${encodeURIComponent(apiDates.attendStart)}&created_at__lte=${encodeURIComponent(apiDates.attendEnd)}`;
        const urlCal = `/hri/calendar_data/?timestart=${apiDates.calStart}&timeend=${apiDates.calEnd}`;
        const urlSmart = `/smart-authen/event/?start_date__gte=${apiDates.smartStart}&start_date__lte=${apiDates.smartEnd}`;

        const promiseList = [
          this.axios.get(urlCal).catch(() => ({ data: [] })),
          this.fetchRequestData(apiDates.calStart, apiDates.calEnd),
          this.axios.get(urlSmart).catch(() => ({ data: [] }))
        ];

        if (this.isStaff) {
          promiseList.push(this.axios.get(urlAttend).catch(() => ({ data: [] })));
        }

        const dataResults = await Promise.all(promiseList);
        
        this.processFetchedData(dataResults);
      } catch (error) {
        console.error('Error fetching calendar data:', error);
      } finally {
        this.loading = false;
      }
    },
    processFetchedData(results) {
      const holidayData = results[0].data || results[0];
      const requestMap = results[1];
      const smartResponse = results[2].data || results[2];
      const attendanceResponse = this.isStaff ? (results[3].data || results[3]) : { data: [] };

      const holidays = Array.isArray(holidayData) ? holidayData : (holidayData.results || []);
      const smartEvents = Array.isArray(smartResponse) ? smartResponse : (smartResponse.results || []);
      const attendanceRecords = Array.isArray(attendanceResponse) ? attendanceResponse : (attendanceResponse.results || []);

      this.calendarDays.forEach(dayItem => {
        const targetDateKey = dayItem.date.format('YYYY-MM-DD');

        dayItem.events = requestMap[targetDateKey] || [];
        dayItem.muicEvents = smartEvents
          .filter(event => event.start_date && event.start_date.startsWith(targetDateKey))
          .sort((a, b) => moment(a.start_date).diff(moment(b.start_date)));

        // Process Holidays
        const holidayRecord = holidays.find(h => h.extra_date === targetDateKey && h.calendar_type === 'holiday');
        if (holidayRecord) {
          dayItem.isHoliday = true;
          dayItem.holidayDetail = holidayRecord.detail ? holidayRecord.detail.split('|')[0].trim() : '';
        }

        // Process Payday
        const paydayRecord = holidays.find(h => h.extra_date === targetDateKey && h.calendar_type === 'payday');
        if (paydayRecord) {
          dayItem.isPayday = true;
          dayItem.paydayDetail = paydayRecord.detail ? paydayRecord.detail.split('|')[0].trim() : '';
        }

        // Process Emergency
        const emergencyRecord = holidays.find(h => h.extra_date === targetDateKey && h.calendar_type === 'emergency');
        if (emergencyRecord) {
          dayItem.isEmergency = true;
          dayItem.emergencyDetail = emergencyRecord.detail ? emergencyRecord.detail.split('|')[0].trim() : '';
        }

        // Process Announce
        const announceRecord = holidays.find(h => h.extra_date === targetDateKey && h.calendar_type === 'announce');
        if (announceRecord) {
          dayItem.isAnnounce = true;
          dayItem.announceDetail = announceRecord.detail ? announceRecord.detail.split('|')[0].trim() : '';
        }

        // Process Attendance
        if (this.isStaff) {
          this.processDayAttendance(dayItem, attendanceRecords, targetDateKey);
        }
      });
    },
    processDayAttendance(dayItem, allRecords, dateKey) {
      const dayRecords = allRecords.filter(record => record.created_at && record.created_at.startsWith(dateKey));

      if (dayRecords.length > 0) {
        dayRecords.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

        const firstRecordTime = dayRecords[0].created_at;
        dayItem.timeIn = moment(firstRecordTime).format('HH:mm:ss');

        if (dayRecords.length > 1) {
          const lastRecordTime = dayRecords[dayRecords.length - 1].created_at;
          dayItem.timeOut = moment(lastRecordTime).format('HH:mm:ss');
          dayItem.isTimeOutEstimated = false;
        } else {
          dayItem.timeOut = moment(firstRecordTime).add(9, 'hours').format('HH:mm:ss');
          dayItem.isTimeOutEstimated = true;
        }
      }
    },
    getEventMeta(type) {
      const metaMap = {
        'Holiday': { icon: 'gift', color: '#eb2f96', bgColor: '#fff0f6' },
        'Leave Of Absence': { icon: 'user-delete', color: '#f5222d', bgColor: '#fff1f0' },
        'Seminar': { icon: 'audit', color: '#13c2c2', bgColor: '#e6fffb' },
        'Work From Home': { icon: 'home', color: '#722ed1', bgColor: '#f9f0ff' },
        'Flexible Day': { icon: 'swap', color: '#1890ff', bgColor: '#e6f7ff' }
      };
      return metaMap[type] || { icon: 'info', color: '#595959', bgColor: '#f0f0f0' };
    },
    checkViewMode() {
      // If width is less than 1200px (where the 7-column grid stops working), 
      // defaulted to list view for better UX.
      const isSmall = window.innerWidth < 1200;
      if (this.lastIsSmall === null || this.lastIsSmall !== isSmall) {
        this.viewMode = isSmall ? 'list' : 'calendar';
        this.lastIsSmall = isSmall;
      }
    }
  }
};
</script>

<style scoped>
.calendar-wrapper {
  padding: 24px;
  border-radius: 12px;
  min-height: 100vh;
}

.calendar-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px 24px;
  border-radius: 12px;
  gap: 16px;
  flex-wrap: wrap;
}

@media (max-width: 768px) {
  .calendar-toolbar {
    flex-direction: column;
    align-items: center;
    padding: 16px;
    gap: 16px;
  }
  .toolbar-nav-group {
    justify-content: space-between;
    width: 100%;
  }
  .toolbar-view-group {
    width: 100%;
    display: flex;
    justify-content: center;
  }
  .calendar-title {
    font-size: 18px;
    min-width: unset;
    flex-grow: 1;
  }
  .btn-text {
    display: none;
  }
  .nav-btn {
    padding: 0 12px;
  }
}

.toolbar-nav-group {
  display: flex;
  align-items: center;
  gap: 16px;
}

.toolbar-view-group {
  display: flex;
  align-items: center;
}

.calendar-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #333;
  min-width: 200px;
  text-align: center;
}

.calendar-title .year {
  color: #888;
  font-weight: 400;
}

.weekday-header {
  text-align: center;
  font-weight: 600;
  color: #333;
  font-size: 15px;
  text-transform: uppercase;
  letter-spacing: 1px;
  background-color: #f2f2f2;
  border: 1px solid #e8e8e8;
  padding: 16px 8px;
  border-radius: 8px;
}

.weekend-text {
  color: #8c8c8c !important; /* Muted Grey */
}

.weekdays-row {
  display: none;
  margin-bottom: 16px;
}

/* Breakpoint for 7-column calendar view */
@media (min-width: 1200px) {
  .weekdays-row {
    display: flex;
  }
  .calendar-col {
    flex: 0 0 14.285714% !important;
    max-width: 14.285714% !important;
    position: relative;
    display: flex;
    flex-direction: column;
  }
  /* Vertical divider between calendar days */
  .calendar-col:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 4px;
    bottom: 4px;
    right: 0;
    width: 2px;
    background-color: #f1f1f1;
    border-radius: 1px;
  }
}

.event-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  display: flex;
  flex-direction: column;
  height: 230px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid #d9d9d9;
}

.event-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(106, 64, 191, 0.15);
}

.faded-card {
  opacity: 0.5;
}

.is-today {
  border: 2px solid #6a40bf;
}

.event-card-header {
  background-color: #7458a8; /* Matched to image purple */
  color: white;
  padding: 8px 12px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  min-height: 60px;
}

.weekend-bg {
  background-color: #e8e8e8 !important;
  color: #555 !important;
}

.holiday-bg {
  background-color: #ff7875 !important;
  color: white !important;
}

.holiday-body {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
  padding: 12px 20px;
  background-color: #fff1f0;
  text-align: center;
}

.holiday-icon {
  font-size: 24px;
  color: #ff4d4f;
  margin-bottom: 8px;
}

.holiday-detail {
  color: #cf1322;
  font-weight: 600;
  font-size: 12px;
}

.is-today .event-card-header {
  background-color: #512da8;
}

.event-card-header .month {
  font-size: 12px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 8px;
  border-radius: 16px;
}

.event-card-header .day {
  font-size: 24px;
  font-weight: 700;
  line-height: 0.9;
  letter-spacing: -1px;
}

.header-tag {
  font-size: 12px;
  font-weight: 700;
  border-radius: 4px;
  border: none;
  height: 20px;
  width: 20px;
  padding: 0;
  display: flex !important;
  align-items: center;
  justify-content: center;
  margin: 0;
  cursor: pointer;
}

.tag-separator {
  color: white;
  margin: 0 4px;
  font-weight: 700;
  font-size: 14px;
}

.event-card-body {
  padding: 12px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.event-title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
}

.event-title-row h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #2c2c2c;
  line-height: 1.2;
}

.event-label {
  border: 1px solid #8e6bc7;
  color: #8e6bc7;
  border-radius: 999px;
  padding: 1px 10px;
  font-size: 11px;
  font-weight: 600;
  background: transparent;
  white-space: nowrap;
  margin-left: 8px;
}

.event-subtitle {
  color: #a4a4a4;
  font-size: 12px;
  margin-bottom: 12px;
  font-weight: 500;
}

.event-desc {
  color: #5a5a5a;
  font-size: 12px;
  line-height: 1.4;
  margin-bottom: 16px;
}

.event-action {
  margin-top: auto;
}

.action-btn {
  background-color: #7458a8;
  border: none;
  font-size: 13px;
  height: 32px;
  padding: 0 16px;
}

.action-btn:hover, .action-btn:focus {
  background-color: #512da8;
  color: white;
}

.event-card-actions {
  display: flex;
  background-color: #f0f2f5;
  border-top: 1px solid #e8e8e8;
}

.action-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 10px 0;
}

.action-item span:first-child {
  color: #888;
  font-size: 11px;
  line-height: 1.2;
}

.action-item span:first-child {
  color: #888;
  font-size: 11px;
  line-height: 1.2;
}

.action-item span:last-child {
  color: #222;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  margin-top: 4px;
}

.action-item:not(:last-child) {
  border-right: 1px solid #e8e8e8;
}

/* Custom Collapse Styling */
::v-deep .custom-event-collapse.ant-collapse {
  background-color: transparent;
}
::v-deep .custom-event-collapse .ant-collapse-item {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background-color: #f8fafc;
  margin-bottom: 0;
  overflow: hidden;
}
::v-deep .custom-event-collapse .ant-collapse-header {
  padding: 6px 10px !important;
  display: flex;
  align-items: center;
}
::v-deep .custom-event-collapse .ant-collapse-content {
  background-color: transparent;
  border-top: 1px solid #e2e8f0;
}
::v-deep .custom-event-collapse .ant-collapse-content-box {
  padding: 8px;
}

.event-list {
  list-style: none;
  padding: 5px;
  margin: 0;
  font-size: 11px;
  color: #555;
}
.event-list li {
  padding: 4px 8px;
  background: white;
  border: 1px solid #ebebeb;
  border-left: 3px solid #6a40bf;
  border-radius: 4px;
  margin-bottom: 4px;
}

.muic-event-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 11px;
  font-weight: 700;
  color: #334155;
  cursor: pointer;
  transition: all 0.2s;
}

.muic-event-trigger:hover {
  background-color: #e2e8f0;
  color: #000;
  border-color: #cbd5e1;
}

.estimated-time {
  color: #b0b0b0 !important;
  font-weight: 500;
}

.truncate-event {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

/* View Toggle Styling */


/* List View Styling */
.list-view-container {
  padding: 24px;
  border-radius: 12px;
}

.list-item-card {
  display: flex;
  align-items: center;
  padding: 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  transition: all 0.3s;
  background: #fafafa;
  margin-bottom: 0px;
}

.list-item-card:hover {
  border-color: #6a40bf;
  box-shadow: 0 2px 8px rgba(106, 64, 191, 0.1);
  background: white;
}

.list-today {
  border-left: 4px solid #6a40bf;
  background: #f7f4ff;
}

.list-weekend {
  background: #fdfdfd;
}

.list-holiday {
  background: #fff1f0;
}

.list-date-info {
  display: flex;
  align-items: center;
  width: 300px;
  flex-shrink: 0;
}

.list-day-num {
  font-size: 32px;
  font-weight: 800;
  color: #334155;
  margin-right: 16px;
  width: 48px;
  text-align: right;
}

.list-month-weekday {
  margin-right: 16px;
}

.list-weekday {
  font-weight: 700;
  font-size: 14px;
  color: #1e293b;
}

.list-month {
  font-size: 12px;
  color: #64748b;
}

.list-tags {
  display: flex;
  gap: 4px;
  flex-direction: column;
}

.list-content-main {
  flex-grow: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
}

.list-events-section {
  flex-grow: 1;
}

.compact-event-row {
  padding: 6px 12px;
  border-radius: 4px;
  margin-bottom: 4px;
  font-size: 13px;
  display: flex;
  align-items: center;
}

.muic-event-row {
  background: #f1f5f9;
  border-left: 4px solid #6a40bf;
  color: #334155;
  font-weight: 500;
}

.holiday-text-list {
  color: #cf1322;
  font-weight: 700;
  font-size: 14px;
}

.list-time-records {
  display: flex;
  gap: 16px;
  min-width: 200px;
}

.time-record-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  min-width: 90px;
}

.time-record-box .label {
  font-size: 10px;
  color: #94a3b8;
  font-weight: 600;
}

.time-record-box .value {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
}

/* List View Mobile Responsiveness */
@media (max-width: 768px) {
  .list-view-container {
    padding: 12px;
  }
  .list-item-card {
    flex-direction: column;
    align-items: flex-start;
    padding: 12px;
  }
  .list-date-info {
    width: 100%;
    margin-bottom: 12px;
    padding-bottom: 12px;
    justify-content: space-between;
  }
  .list-day-num {
    font-size: 28px;
    width: auto;
    margin-right: 12px;
  }
  .list-content-main {
    flex-direction: column;
    width: 100%;
    gap: 16px;
    align-items: flex-start;
  }
  .list-events-section {
    width: 100%;
  }
  .list-time-records {
    width: 100%;
    justify-content: space-between;
    min-width: unset;
    gap: 8px;
  }
  .time-record-box {
    flex: 1;
    padding: 6px 10px;
  }
}
</style>